/**
 * Admin · Deleted calls: calls users deleted from their history. A deleted
 * call keeps its recording, transcript and prompt for a review window (24
 * hours), viewable from its call page, and is then purged automatically; it
 * can be purged early here. Purged calls keep only their accounting facts
 * (see call_deletion.py).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, DeletedCallRow, DeletedCallsListing } from './adminApi';
import { fmtDuration } from './AdminPnl';
import { fmtAdminDateTime } from './timeFormat';
import './Admin.css';

function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback;
}

function Who({ row }: { row: DeletedCallRow }) {
  if (!row.userId) return <>—</>;
  return <Link to={`/admin/users/${row.userId}`}>{row.userEmail || row.userId}</Link>;
}

function CallFacts({ row }: { row: DeletedCallRow }) {
  return (
    <>
      {fmtAdminDateTime(row.initiatedAt)}
      <div className="admin-muted">
        {row.toNumber || '—'} · {row.status || 'unknown'} · {row.duration != null ? fmtDuration(row.duration) : '—'} ·
        credit {row.creditDeducted ? 'deducted' : 'not deducted'}
      </div>
    </>
  );
}

function AdminDeletedCalls() {
  const [listing, setListing] = useState<DeletedCallsListing | null>(null);
  const [error, setError] = useState('');
  const [purging, setPurging] = useState<string | null>(null);

  const load = useCallback(() => {
    setError('');
    adminApi
      .deletedCalls()
      .then(setListing)
      .catch((e) => setError(errorMessage(e, 'Failed to load deleted calls')));
  }, []);

  useEffect(load, [load]);

  const purgeNow = async (row: DeletedCallRow) => {
    if (!window.confirm(`Erase the recording, transcript and prompt of this call now? This cannot be undone.`)) return;
    setPurging(row.callId);
    setError('');
    try {
      await adminApi.purgeDeletedCall(row.callId);
      load();
    } catch (e) {
      setError(errorMessage(e, 'Failed to purge the call'));
    } finally {
      setPurging(null);
    }
  };

  if (!listing) {
    return error ? <div className="admin-error">{error}</div> : <div className="admin-muted">Loading…</div>;
  }

  return (
    <div>
      {error && <div className="admin-error">{error}</div>}

      <div className="admin-section">
        <h2>Pending review ({listing.pending.length})</h2>
        <p className="admin-muted">
          Hidden from the user. The recording, transcript and prompt are kept for {listing.reviewWindowHours} hours
          after the request, then erased automatically.
        </p>
        {listing.pending.length === 0 ? (
          <div className="admin-muted">Nothing to review.</div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table static">
              <thead>
                <tr>
                  <th>Deleted</th>
                  <th>Who</th>
                  <th>Call</th>
                  <th>Content</th>
                  <th>Erased at</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {listing.pending.map((row) => (
                  <tr key={row.callId}>
                    <td>{fmtAdminDateTime(row.deletionRequestedAt)}</td>
                    <td>
                      <Who row={row} />
                    </td>
                    <td>
                      <CallFacts row={row} />
                    </td>
                    <td>{[row.hasAudio && 'audio', row.hasTranscript && 'transcript'].filter(Boolean).join(', ') || '—'}</td>
                    <td>{fmtAdminDateTime(row.purgeAfter)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <Link className="admin-inline-btn" to={`/admin/calls/${row.callId}`}>
                        Review
                      </Link>{' '}
                      <button
                        className="admin-inline-btn"
                        disabled={purging !== null}
                        onClick={() => purgeNow(row)}
                      >
                        {purging === row.callId ? 'Erasing…' : 'Erase now'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="admin-section">
        <h2>Erased ({listing.purged.length})</h2>
        {listing.purged.length === 0 ? (
          <div className="admin-muted">No erased calls yet.</div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table static">
              <thead>
                <tr>
                  <th>Deleted</th>
                  <th>Who</th>
                  <th>Call</th>
                  <th>Erased</th>
                </tr>
              </thead>
              <tbody>
                {listing.purged.map((row) => (
                  <tr key={row.callId}>
                    <td>{fmtAdminDateTime(row.deletionRequestedAt)}</td>
                    <td>
                      <Who row={row} />
                    </td>
                    <td>
                      <CallFacts row={row} />
                    </td>
                    <td>
                      {fmtAdminDateTime(row.contentPurgedAt)}
                      <div className="admin-muted">
                        {row.contentPurgedBy === 'auto' ? 'automatically' : `by ${row.contentPurgedBy || 'unknown'}`}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDeletedCalls;
